'use client'

import { useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { toast } from '@/lib/toast'
import { navigateUrl } from '@/utils/client-request'
import { checkOrderPaymentApi, checkGiftCardPaymentApi, type PaymentEndpoint } from '@/lib/commerce/services/payment-api'

export type { PaymentEndpoint } from '@/lib/commerce/services/payment-api'

type Args = {
  order: any
  vendor: any
  method: PaymentEndpoint
  onRefresh: () => void
}

export function usePaymentChecker({ order, vendor, method, onRefresh }: Args) {
  const router = useRouter()
  const pathname = usePathname()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const hasPaymentRef = () => {
    if (!order || !order.id) return false
    if (method === 'pocket') return !!(order.pocket?.id && order.pocket?.qr)
    if (method === 'omniway') return !!(order.omniway?.invoiceNumber && !order.omniway?.canceledAt)
    return !!(order as any)[method]?.id
  }

  const check = async () => {
    if (success) {
      /*
        /order/{customId} requires a session — a guest landing there is bounced to the
        order-lookup screen. Keep the /{phone} segment they arrived on, which is the
        public route. This was rare while every order was paid once at checkout; with
        split payment a guest returning days later to pay a balance is the common path.
      */
      const target = order?.phone && pathname?.includes(`/${order.phone}`)
        ? `/order/${order.customId}/${order.phone}`
        : `/order/${order.customId}`
      router.push(navigateUrl(target, pathname))
      return
    }
    if (!hasPaymentRef()) return toast.error('Төлбөрийн мэдээлэл олдсонгүй')
    setLoading(true)
    const { data, error } = await checkOrderPaymentApi(method, order.id, vendor.id)
    setLoading(false)
    if (error) return toast.error(error)
    if (method === 'qpay' && data === undefined) return toast.error('Төлбөрийн мэдээлэл олдсонгүй')
    if (data === true) {
      setSuccess(true)
      onRefresh()
      return toast.success('Төлбөр амжилттай төлөгдлөө')
    }
    if (method === 'qpay' && data === false) return toast.error('Төлбөр төлөгдөөгүй байна')
    return toast.error('Төлбөр хүлээгдэж байна')
  }

  return { loading, success, check }
}

export function useGiftCardPaymentChecker({ giftCard, onSuccess }: { giftCard: any; onSuccess: () => void }) {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const check = async () => {
    if (!giftCard?.id || !giftCard?.qpay?.id) {
      toast.error('Төлбөрийн мэдээлэл олдсонгүй')
      return
    }
    setLoading(true)
    const { data, error } = await checkGiftCardPaymentApi(giftCard.id, giftCard.vendor.id)
    setLoading(false)
    if (error) return toast.error(error)
    if (data === undefined) return toast.error('Төлбөрийн мэдээлэл олдсонгүй')
    if (data === false) return toast.error('Төлбөр төлөгдөөгүй байна')
    if (data === true) {
      setSuccess(true)
      onSuccess()
    }
  }

  return { loading, success, check }
}
